export function createFundingSettings() {
  return {
    funding_enabled: false,
    slug: "",
    slug_locked: false,
    promotion_code: "",
    visibility: "private",
    display_order: 0,
    goal_amount_krw: 500000,
    early_bird_limit: 0,
    early_bird_discount_amount: 0,
    funding_starts_on: "",
    funding_ends_on: "",
  }
}

export function campaignToFundingSettings(campaign) {
  if (!campaign) return createFundingSettings()

  return {
    ...createFundingSettings(),
    funding_enabled: Boolean(campaign.funding_enabled),
    slug: campaign.slug || "",
    slug_locked: Boolean(campaign.slug_locked || campaign.published_at),
    promotion_code: campaign.promotion_code || "",
    visibility: campaign.visibility === "public" ? "public" : "private",
    display_order: Number(campaign.display_order || 0),
    goal_amount_krw: Number(campaign.goal_amount_krw || 500000),
    early_bird_limit: Number(campaign.early_bird_limit || 0),
    early_bird_discount_amount: Number(campaign.early_bird_discount_amount || 0),
    funding_starts_on: campaign.funding_starts_on || "",
    funding_ends_on: campaign.funding_ends_on || "",
  }
}

export function fundingPayload(funding, conceptId) {
  const payload = {
    concept_id: Number(conceptId),
    funding_enabled: Boolean(funding.funding_enabled),
  }

  if (!funding.funding_enabled) return payload

  return {
    ...payload,
    slug: funding.slug.trim(),
    promotion_code: funding.promotion_code.trim(),
    visibility: funding.visibility,
    display_order: Number(funding.display_order || 0),
    goal_amount_krw: Number(funding.goal_amount_krw),
    early_bird_limit: Number(funding.early_bird_limit || 0),
    early_bird_discount_amount: Number(funding.early_bird_discount_amount || 0),
  }
}

function parseDateOnly(value) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(value || ""))) return null
  const parts = value.split("-").map(Number)
  const date = new Date(Date.UTC(parts[0], parts[1] - 1, parts[2]))
  return date.getUTCFullYear() === parts[0] &&
    date.getUTCMonth() === parts[1] - 1 &&
    date.getUTCDate() === parts[2]
    ? date
    : null
}

function formatDateOnly(date) {
  return [
    String(date.getUTCFullYear()).padStart(4, "0"),
    String(date.getUTCMonth() + 1).padStart(2, "0"),
    String(date.getUTCDate()).padStart(2, "0"),
  ].join("-")
}

export function buildFundingSchedule(fundingStartsOn, fundingEndsOn) {
  const fundingStart = parseDateOnly(fundingStartsOn)
  const fundingEnd = parseDateOnly(fundingEndsOn)
  if (!fundingStart || !fundingEnd || fundingEnd < fundingStart) return null
  const visitStart = new Date(fundingEnd.getTime())
  visitStart.setUTCDate(visitStart.getUTCDate() + 1)
  const visitEnd = new Date(visitStart.getTime())
  visitEnd.setUTCMonth(visitEnd.getUTCMonth() + 1)
  visitEnd.setUTCDate(visitEnd.getUTCDate() - 1)
  return {
    openAt: formatDateOnly(fundingStart),
    closeAt: formatDateOnly(fundingEnd),
    startAt: formatDateOnly(visitStart),
    endAt: formatDateOnly(visitEnd),
  }
}

async function loadPromotion(http, promotionCode) {
  const response = await http.get("/api/promotion/select/code", {
    params: { code: promotionCode, raw: 1 },
  })
  const rows = Array.isArray(response.data) ? response.data : []
  return rows.find(item => item && item.code === promotionCode) || null
}

async function savePromotionSchedule(http, promotion, schedule) {
  if (!promotion || !schedule) throw new Error("Promotion schedule is missing")
  return http.post("/api/promotion/update", {
    params: {
      code: promotion.code,
      name: promotion.name,
      option1Price: promotion.option1Price,
      option2Price: promotion.option2Price,
      option3Price: promotion.option3Price,
      option4Price: promotion.option4Price,
      option5Price: promotion.option5Price,
      option6Price: promotion.option6Price,
      conceptId: promotion.conceptId,
      role: promotion.role,
      enabled: promotion.enabled,
      ...schedule,
      description: promotion.content,
      description_en: promotion.content_en,
      description_jp: promotion.content_jp,
      isIndividual: promotion.isIndividual,
      incentive_percent: promotion.incentive_percent,
      email: promotion.email,
      phone: promotion.phone,
      bank: promotion.bank,
      account_number: promotion.account_number,
      products: promotion.products,
      optionProducts: promotion.option_products,
      id: promotion.id,
    },
  })
}

export async function loadFundingSettings(http, conceptId) {
  try {
    const response = await http.get(`/api/admin/funding/projects/by-concept/${conceptId}`)
    return {
      exists: Boolean(response.data && response.data.campaign),
      funding: campaignToFundingSettings(response.data && response.data.campaign),
    }
  } catch (error) {
    if (error.response && error.response.status === 404) {
      return { exists: false, funding: createFundingSettings() }
    }
    throw error
  }
}

export async function saveFundingSettings(http, conceptId, funding) {
  const saved = await http.put(
    `/api/admin/funding/projects/by-concept/${conceptId}`,
    fundingPayload(funding, conceptId)
  )
  if (!funding.funding_enabled) return saved
  const schedule = buildFundingSchedule(
    funding.funding_starts_on,
    funding.funding_ends_on
  )
  const promotion = await loadPromotion(http, funding.promotion_code)
  await savePromotionSchedule(http, promotion, schedule)
  return saved
}
